import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Target, Search, Plus, LayoutGrid, List, Phone, Mail, Trash2,
  GripVertical, MessageSquare, Star, Send, ChevronDown, MoreHorizontal,
  User, Calendar, Tag, ArrowUpDown
} from "lucide-react";

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [stages, setStages] = useState([]);
  const [stageStats, setStageStats] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("kanban");
  const [search, setSearch] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [filterStage, setFilterStage] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [showFollowupDialog, setShowFollowupDialog] = useState(false);

  const [newLead, setNewLead] = useState({
    name: "", phone: "", email: "", source: "manual", notes: "", product_interest: "", budget: ""
  });
  const [newActivity, setNewActivity] = useState({ type: "note", content: "" });
  const [followupMsg, setFollowupMsg] = useState("");
  const [followupChannel, setFollowupChannel] = useState("whatsapp");

  const fetchLeads = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (filterSource) params.append("source", filterSource);
      if (filterStage) params.append("stage_id", filterStage);
      params.append("limit", "200");
      const res = await api.get(`/tenant/leads?${params}`);
      setLeads(res.data.leads || []);
      setStages(res.data.stages || []);
      setStageStats(res.data.stage_stats || {});
    } catch {
      toast.error("Gagal memuat leads");
    } finally {
      setLoading(false);
    }
  }, [search, filterSource, filterStage]);

  const fetchStats = async () => {
    try {
      const res = await api.get("/tenant/lead-stats");
      setStats(res.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchLeads(); fetchStats(); }, [fetchLeads]);

  const handleAddLead = async () => {
    if (!newLead.name.trim()) { toast.error("Nama wajib diisi"); return; }
    try {
      await api.post("/tenant/leads", {
        ...newLead,
        budget: newLead.budget ? parseFloat(newLead.budget) : null
      });
      toast.success("Lead berhasil ditambahkan");
      setShowAddDialog(false);
      setNewLead({ name: "", phone: "", email: "", source: "manual", notes: "", product_interest: "", budget: "" });
      fetchLeads(); fetchStats();
    } catch {
      toast.error("Gagal menambahkan lead");
    }
  };

  const openDetail = async (lead) => {
    setSelectedLead(lead);
    setShowDetailDialog(true);
    try {
      const res = await api.get(`/tenant/leads/${lead.id}`);
      setSelectedLead(res.data.lead);
      setActivities(res.data.activities || []);
    } catch { /* ignore */ }
  };

  const handleStageChange = async (leadId, newStageId) => {
    try {
      await api.put(`/tenant/leads/${leadId}`, { stage_id: newStageId });
      fetchLeads();
      if (selectedLead?.id === leadId) {
        const stage = stages.find(s => s.id === newStageId);
        setSelectedLead(prev => ({ ...prev, stage_id: newStageId, stage_name: stage?.name }));
      }
    } catch {
      toast.error("Gagal mengubah stage");
    }
  };

  const handleDeleteLead = async (leadId) => {
    try {
      await api.delete(`/tenant/leads/${leadId}`);
      toast.success("Lead dihapus");
      setShowDetailDialog(false);
      fetchLeads(); fetchStats();
    } catch {
      toast.error("Gagal menghapus lead");
    }
  };

  const handleAddActivity = async () => {
    if (!newActivity.content.trim() || !selectedLead) return;
    try {
      const res = await api.post(`/tenant/leads/${selectedLead.id}/activities`, newActivity);
      setActivities(prev => [res.data, ...prev]);
      setNewActivity({ type: "note", content: "" });
    } catch {
      toast.error("Gagal menambahkan aktivitas");
    }
  };

  const handleSendFollowup = async () => {
    if (!followupMsg.trim() || !selectedLead) return;
    try {
      await api.post(`/tenant/leads/${selectedLead.id}/send-followup?channel=${followupChannel}&message=${encodeURIComponent(followupMsg)}`);
      toast.success("Follow-up terkirim");
      setShowFollowupDialog(false);
      setFollowupMsg("");
      openDetail(selectedLead);
    } catch {
      toast.error("Gagal mengirim follow-up");
    }
  };

  const handleBulkStage = async (stageId) => {
    if (selectedLeads.length === 0) return;
    try {
      await api.post("/tenant/leads/bulk/update-stage", { lead_ids: selectedLeads, stage_id: stageId });
      toast.success(`${selectedLeads.length} lead dipindahkan`);
      setSelectedLeads([]);
      fetchLeads();
    } catch {
      toast.error("Gagal update bulk");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedLeads.length === 0) return;
    try {
      await api.post("/tenant/leads/bulk/delete", { lead_ids: selectedLeads });
      toast.success(`${selectedLeads.length} lead dihapus`);
      setSelectedLeads([]);
      fetchLeads(); fetchStats();
    } catch {
      toast.error("Gagal hapus bulk");
    }
  };

  // Drag and Drop
  const handleDragStart = (e, leadId) => {
    e.dataTransfer.setData("text/plain", leadId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDrop = (e, stageId) => {
    e.preventDefault();
    const leadId = e.dataTransfer.getData("text/plain");
    if (leadId) handleStageChange(leadId, stageId);
  };

  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };

  const getScoreColor = (score) => {
    if (score >= 70) return "text-green-600 bg-green-50";
    if (score >= 40) return "text-yellow-600 bg-yellow-50";
    return "text-gray-500 bg-gray-50";
  };

  const formatDate = (d) => {
    if (!d) return "-";
    return new Date(d).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" });
  };

  const formatTime = (d) => {
    if (!d) return "";
    return new Date(d).toLocaleString("id-ID", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  };

  if (loading) return <div className="p-6 text-center text-muted-foreground">Memuat...</div>;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Target className="w-6 h-6" /> Lead Management</h1>
          <p className="text-muted-foreground text-sm">Kelola prospek dan pipeline penjualan</p>
        </div>
        <Button onClick={() => setShowAddDialog(true)}><Plus className="w-4 h-4 mr-2" /> Tambah Lead</Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Total Lead</p><p className="text-2xl font-bold">{stats.total}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Bulan Ini</p><p className="text-2xl font-bold">{stats.this_month}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Converted</p><p className="text-2xl font-bold text-green-600">{stats.converted}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Konversi</p><p className="text-2xl font-bold">{stats.conversion_rate}%</p></CardContent></Card>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Cari lead..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={filterSource} onValueChange={v => setFilterSource(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="Sumber" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Sumber</SelectItem>
            <SelectItem value="form">Form</SelectItem>
            <SelectItem value="manual">Manual</SelectItem>
            <SelectItem value="whatsapp">WhatsApp</SelectItem>
            <SelectItem value="referral">Referral</SelectItem>
            <SelectItem value="social_media">Social Media</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex border rounded-md">
          <Button variant={view === "kanban" ? "secondary" : "ghost"} size="sm" onClick={() => setView("kanban")}><LayoutGrid className="w-4 h-4" /></Button>
          <Button variant={view === "table" ? "secondary" : "ghost"} size="sm" onClick={() => setView("table")}><List className="w-4 h-4" /></Button>
        </div>

        {selectedLeads.length > 0 && (
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-sm text-muted-foreground">{selectedLeads.length} dipilih</span>
            <Select onValueChange={handleBulkStage}>
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="Pindah ke..." /></SelectTrigger>
              <SelectContent>
                {stages.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="destructive" size="sm" onClick={handleBulkDelete}><Trash2 className="w-4 h-4" /></Button>
          </div>
        )}
      </div>

      {/* Kanban View */}
      {view === "kanban" && (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {stages.map(stage => {
            const stageLeads = leads.filter(l => l.stage_id === stage.id);
            return (
              <div
                key={stage.id}
                className="flex-shrink-0 w-[300px] bg-muted/30 rounded-lg"
                onDragOver={handleDragOver}
                onDrop={e => handleDrop(e, stage.id)}
              >
                <div className="p-3 border-b flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stage.color }} />
                    <span className="font-medium text-sm">{stage.name}</span>
                  </div>
                  <Badge variant="secondary" className="text-xs">{stageStats[stage.id] || 0}</Badge>
                </div>
                <div className="p-2 space-y-2 min-h-[200px] max-h-[calc(100vh-400px)] overflow-y-auto">
                  {stageLeads.map(lead => (
                    <div
                      key={lead.id}
                      draggable
                      onDragStart={e => handleDragStart(e, lead.id)}
                      onClick={() => openDetail(lead)}
                      className="bg-background p-3 rounded-md border cursor-pointer hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between">
                        <p className="font-medium text-sm">{lead.name}</p>
                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${getScoreColor(lead.score)}`}>
                          {lead.score}
                        </span>
                      </div>
                      {lead.product_interest && (
                        <p className="text-xs text-muted-foreground mt-1 truncate">{lead.product_interest}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                        {lead.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{lead.phone}</span>}
                        {lead.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{lead.email.split("@")[0]}</span>}
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-muted-foreground">{formatDate(lead.created_at)}</span>
                        {lead.source && <Badge variant="outline" className="text-xs">{lead.source}</Badge>}
                      </div>
                    </div>
                  ))}
                  {stageLeads.length === 0 && (
                    <p className="text-xs text-center text-muted-foreground py-8">Tidak ada lead</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Table View */}
      {view === "table" && (
        <div className="border rounded-lg overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="p-3 text-left w-8">
                  <input type="checkbox" onChange={e => setSelectedLeads(e.target.checked ? leads.map(l => l.id) : [])}
                    checked={selectedLeads.length === leads.length && leads.length > 0} />
                </th>
                <th className="p-3 text-left">Nama</th>
                <th className="p-3 text-left">Kontak</th>
                <th className="p-3 text-left">Stage</th>
                <th className="p-3 text-left">Source</th>
                <th className="p-3 text-left">Score</th>
                <th className="p-3 text-left">Minat</th>
                <th className="p-3 text-left">Tanggal</th>
              </tr>
            </thead>
            <tbody>
              {leads.map(lead => (
                <tr key={lead.id} className="border-t hover:bg-muted/30 cursor-pointer" onClick={() => openDetail(lead)}>
                  <td className="p-3" onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={selectedLeads.includes(lead.id)}
                      onChange={e => setSelectedLeads(prev => e.target.checked ? [...prev, lead.id] : prev.filter(id => id !== lead.id))} />
                  </td>
                  <td className="p-3 font-medium">{lead.name}</td>
                  <td className="p-3">
                    <div className="space-y-0.5">
                      {lead.phone && <div className="flex items-center gap-1 text-xs"><Phone className="w-3 h-3" />{lead.phone}</div>}
                      {lead.email && <div className="flex items-center gap-1 text-xs"><Mail className="w-3 h-3" />{lead.email}</div>}
                    </div>
                  </td>
                  <td className="p-3">
                    <Badge style={{ backgroundColor: stages.find(s => s.id === lead.stage_id)?.color + "20", color: stages.find(s => s.id === lead.stage_id)?.color }}>
                      {lead.stage_name}
                    </Badge>
                  </td>
                  <td className="p-3"><Badge variant="outline">{lead.source || "-"}</Badge></td>
                  <td className="p-3">
                    <span className={`text-xs px-2 py-1 rounded font-medium ${getScoreColor(lead.score)}`}>{lead.score}</span>
                  </td>
                  <td className="p-3 text-xs max-w-[150px] truncate">{lead.product_interest || "-"}</td>
                  <td className="p-3 text-xs text-muted-foreground">{formatDate(lead.created_at)}</td>
                </tr>
              ))}
              {leads.length === 0 && (
                <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">Belum ada lead</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Lead Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tambah Lead Baru</DialogTitle>
            <DialogDescription>Masukkan informasi prospek baru</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Nama *" value={newLead.name} onChange={e => setNewLead(p => ({ ...p, name: e.target.value }))} />
            <Input placeholder="No. Telepon" value={newLead.phone} onChange={e => setNewLead(p => ({ ...p, phone: e.target.value }))} />
            <Input placeholder="Email" value={newLead.email} onChange={e => setNewLead(p => ({ ...p, email: e.target.value }))} />
            <Input placeholder="Minat Produk/Jasa" value={newLead.product_interest} onChange={e => setNewLead(p => ({ ...p, product_interest: e.target.value }))} />
            <Input placeholder="Budget (Rp)" type="number" value={newLead.budget} onChange={e => setNewLead(p => ({ ...p, budget: e.target.value }))} />
            <Select value={newLead.source} onValueChange={v => setNewLead(p => ({ ...p, source: v }))}>
              <SelectTrigger><SelectValue placeholder="Sumber lead" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">Manual</SelectItem>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                <SelectItem value="referral">Referral</SelectItem>
                <SelectItem value="social_media">Social Media</SelectItem>
                <SelectItem value="website">Website</SelectItem>
                <SelectItem value="other">Lainnya</SelectItem>
              </SelectContent>
            </Select>
            <Textarea placeholder="Catatan" value={newLead.notes} onChange={e => setNewLead(p => ({ ...p, notes: e.target.value }))} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Batal</Button>
            <Button onClick={handleAddLead}>Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lead Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedLead && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between">
                  <span>{selectedLead.name}</span>
                  <span className={`text-sm px-2 py-1 rounded ${getScoreColor(selectedLead.score)}`}>
                    Score: {selectedLead.score}
                  </span>
                </DialogTitle>
                <DialogDescription>Detail dan riwayat lead</DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-muted-foreground">Telepon</label>
                    <p className="text-sm">{selectedLead.phone || "-"}</p>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Email</label>
                    <p className="text-sm">{selectedLead.email || "-"}</p>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Minat</label>
                    <p className="text-sm">{selectedLead.product_interest || "-"}</p>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Budget</label>
                    <p className="text-sm">{selectedLead.budget ? `Rp ${selectedLead.budget.toLocaleString("id-ID")}` : "-"}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-muted-foreground">Stage</label>
                    <Select value={selectedLead.stage_id || ""} onValueChange={v => handleStageChange(selectedLead.id, v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {stages.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Sumber</label>
                    <p className="text-sm">{selectedLead.source || "-"}</p>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Tanggal Masuk</label>
                    <p className="text-sm">{formatDate(selectedLead.created_at)}</p>
                  </div>
                  {selectedLead.notes && (
                    <div>
                      <label className="text-xs text-muted-foreground">Catatan</label>
                      <p className="text-sm">{selectedLead.notes}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="flex gap-2 border-t pt-4">
                <Button size="sm" variant="outline" onClick={() => setShowFollowupDialog(true)}>
                  <Send className="w-4 h-4 mr-1" /> Kirim Follow-up
                </Button>
                <Button size="sm" variant="destructive" onClick={() => handleDeleteLead(selectedLead.id)}>
                  <Trash2 className="w-4 h-4 mr-1" /> Hapus
                </Button>
              </div>

              {/* Add Activity */}
              <div className="border-t pt-4 space-y-2">
                <p className="text-sm font-medium">Tambah Aktivitas</p>
                <div className="flex gap-2">
                  <Select value={newActivity.type} onValueChange={v => setNewActivity(p => ({ ...p, type: v }))}>
                    <SelectTrigger className="w-[120px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="note">Catatan</SelectItem>
                      <SelectItem value="call">Telepon</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      <SelectItem value="meeting">Meeting</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input placeholder="Isi aktivitas..." value={newActivity.content}
                    onChange={e => setNewActivity(p => ({ ...p, content: e.target.value }))}
                    onKeyDown={e => e.key === "Enter" && handleAddActivity()} className="flex-1" />
                  <Button size="sm" onClick={handleAddActivity}>Tambah</Button>
                </div>
              </div>

              {/* Activity Timeline */}
              <div className="border-t pt-4 space-y-3 max-h-[300px] overflow-y-auto">
                <p className="text-sm font-medium">Riwayat ({activities.length})</p>
                {activities.map(a => (
                  <div key={a.id} className="flex gap-3 text-sm">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                      {a.type === "note" && <MessageSquare className="w-4 h-4" />}
                      {a.type === "call" && <Phone className="w-4 h-4" />}
                      {a.type === "email" && <Mail className="w-4 h-4" />}
                      {a.type === "whatsapp" && <Send className="w-4 h-4" />}
                      {a.type === "meeting" && <User className="w-4 h-4" />}
                      {["created", "updated", "auto_followup"].includes(a.type) && <Star className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm">{a.content}</p>
                      <p className="text-xs text-muted-foreground">{formatTime(a.created_at)} · {a.created_by_name || a.created_by || "System"}</p>
                    </div>
                  </div>
                ))}
                {activities.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">Belum ada aktivitas</p>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Follow-up Dialog */}
      <Dialog open={showFollowupDialog} onOpenChange={setShowFollowupDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kirim Follow-up</DialogTitle>
            <DialogDescription>Kirim pesan ke {selectedLead?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Select value={followupChannel} onValueChange={setFollowupChannel}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="both">Keduanya</SelectItem>
              </SelectContent>
            </Select>
            <Textarea placeholder="Tulis pesan follow-up..." value={followupMsg} onChange={e => setFollowupMsg(e.target.value)} rows={4} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFollowupDialog(false)}>Batal</Button>
            <Button onClick={handleSendFollowup}><Send className="w-4 h-4 mr-2" /> Kirim</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
